import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

interface AboutProps {
  setCurrentPage?: (page: string) => void;
}

export default function About({ setCurrentPage }: AboutProps) {
  const handleReadMore = () => {
    window.location.hash = '#/about';
    setCurrentPage?.('about');
  };

  return (
    <section id="about" style={{ padding: '6rem 2rem', background: 'linear-gradient(180deg, transparent, rgba(255,107,0,0.02), transparent)' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4rem', alignItems: 'center' }}>
          {/* Left: Background intro */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            <span style={{
              display: 'inline-block', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.14em',
              textTransform: 'uppercase', color: 'var(--text)', marginBottom: '1rem',
              padding: '0.3rem 0.9rem', borderRadius: 100,
              background: 'var(--electric-dim)', border: '1px solid var(--gray-light)',
            }}>Our Background</span>
            <h2 style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.75rem)', marginBottom: '1.5rem', letterSpacing: '-0.02em', color: 'var(--text)' }}>
              Building Trust Through <span style={{ color: 'var(--orange)' }}>Technology</span>
            </h2>
            <p style={{ fontSize: '1rem', color: 'var(--text-soft)', lineHeight: 1.8, marginBottom: '1.5rem' }}>
              Founded in March 2010, Findura Infosys Limited was created to offer innovative ICT solutions that simplify complex technologies, making them more accessible and user-friendly for customers.
            </p>
            <p style={{ fontSize: '1rem', color: 'var(--text-soft)', lineHeight: 1.8, marginBottom: '2rem' }}>
              As a verified Microsoft Partner and trusted IT advisor, we've become a recognized provider of software solutions across web, desktop, and mobile platforms—committed to empowering African businesses with cutting-edge, cost-effective technology.
            </p>

            <button
              onClick={handleReadMore}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                padding: '0.875rem 2rem', borderRadius: 12,
                background: 'var(--orange)', color: 'var(--white)',
                fontFamily: 'Syne', fontWeight: 700, fontSize: '0.9rem',
                border: 'none',
                boxShadow: '0 0 30px rgba(255, 107, 0, 0.2)',
                transition: 'all 0.25s',
                cursor: 'pointer',
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 0 50px rgba(255, 107, 0, 0.4)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'none'; (e.currentTarget as HTMLElement).style.boxShadow = '0 0 30px rgba(255, 107, 0, 0.2)'; }}
            >
              Read More <ArrowRight size={16} />
            </button>
          </motion.div>

          {/* Right: Quick stats */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            style={{
              padding: '2.5rem',
              background: 'rgba(255,255,255,0.6)',
              border: '1px solid var(--gray-light)',
              borderRadius: 20,
            }}
          >
            <div style={{ display: 'grid', gap: '2rem' }}>
              <div>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--orange)', marginBottom: '0.5rem' }}>Founded</div>
                <div style={{ fontSize: '2rem', fontWeight: 700, color: '#000000' }}>2010</div>
                <div style={{ fontSize: '0.9rem', color: 'var(--text-soft)', marginTop: '0.25rem' }}>March 15th, Lusaka</div>
              </div>
              <div style={{ borderTop: '1px solid var(--gray-light)', paddingTop: '1.5rem' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--orange)', marginBottom: '0.5rem' }}>Our Commitment</div>
                <p style={{ fontSize: '0.95rem', color: 'var(--text)', lineHeight: 1.7 }}>
                  Providing high-quality, cost-effective IT solutions that give businesses a competitive edge across Africa.
                </p>
              </div>
              <div style={{ borderTop: '1px solid var(--gray-light)', paddingTop: '1.5rem' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--orange)', marginBottom: '1rem' }}>Certifications</div>
                <div style={{ display: 'grid', gap: '0.5rem' }}>
                  {['Microsoft Partner', 'NAPSA Registered', 'CEEC Certified'].map(cert => (
                    <div key={cert} style={{
                      fontSize: '0.85rem', color: 'var(--text)',
                      padding: '0.5rem 0.75rem', borderRadius: 6,
                      background: 'var(--electric-dim)', border: '1px solid var(--gray-light)',
                    }}>✓ {cert}</div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          #about > div > div { grid-template-columns: 1fr !important; gap: 2.5rem !important; }
        }
      `}</style>
    </section>
  );
}
